import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import {
  Badge,
  Button,
  Card,
  Dialog,
  Icon,
  Input,
  Metric,
  Switcher,
  Table,
  type Column,
} from '@/components/primitives';
import { PageHeader } from '@/components/layout/PageHeader';
import { useViewport } from '@/lib/viewport';
import { useAuth } from '@/lib/auth';
import { useScope } from '@/lib/scope';
import { useCurrentConfig } from '@/hooks/useConfig';
import { useCustomers } from '@/hooks/useCustomers';
import { configService } from '@/services/configService';
import { auditService } from '@/services/auditService';
import {
  parseSpreadsheet,
  buildSheet,
  downloadWorkbook,
  type ParseRow,
} from '@/services/xlsxParser';
import type { ConfigValue, Customer, PkType, ProductGroup } from '@/domain';
import { ProductGroupSchema, PkTypeSchema } from '@/domain';

// ─── Constants ───────────────────────────────────────────────────────────────

const PRODUCT_GROUPS: ProductGroup[] = ['COSMETIC', 'FMCG', 'ELECTRONICS', 'APPAREL', 'OTHER'];
const PK_TYPES: PkType[] = ['STANDARD', 'BULKY', 'FRAGILE', 'COMBO'];
const TOTAL_CELLS = PRODUCT_GROUPS.length * PK_TYPES.length; // 20

const PG_LABEL: Record<ProductGroup, string> = {
  COSMETIC: 'Mỹ phẩm',
  FMCG: 'FMCG',
  ELECTRONICS: 'Điện tử',
  APPAREL: 'Thời trang',
  OTHER: 'Khác',
};
const PK_LABEL: Record<PkType, string> = {
  STANDARD: 'Tiêu chuẩn',
  BULKY: 'Cồng kềnh',
  FRAGILE: 'Dễ vỡ',
  COMBO: 'Combo',
};

// ─── Import schema ────────────────────────────────────────────────────────────

const UphImportRowSchema = z.object({
  product_group: ProductGroupSchema,
  pk_type: PkTypeSchema,
  uph: z.coerce.number().int().positive(),
});
type UphImportRow = z.infer<typeof UphImportRowSchema>;

const IMPORT_HEADER_MAP: Record<string, string> = {
  'Nhóm hàng': 'product_group',
  'Loại pack': 'pk_type',
  UPH: 'uph',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

type CoverageStatus = 'full' | 'partial' | 'none';

interface Coverage {
  count: number;
  status: CoverageStatus;
}

function computeCoverage(
  norms: ConfigValue[],
  warehouseId: string,
  customerId: string,
): Coverage {
  const count = norms.filter((n) => {
    const parts = n.key.split(':');
    return parts[1] === warehouseId && parts[2] === customerId;
  }).length;
  return {
    count,
    status: count === 0 ? 'none' : count >= TOTAL_CELLS ? 'full' : 'partial',
  };
}

const COVERAGE_BADGE: Record<CoverageStatus, { label: string; kind: 'SUCCESS' | 'WARNING' | 'INFO' }> = {
  full: { label: 'Đầy đủ', kind: 'SUCCESS' },
  partial: { label: 'Một phần', kind: 'WARNING' },
  none: { label: 'Chưa có', kind: 'INFO' },
};

// ─── Cell edit state ──────────────────────────────────────────────────────────

interface CellEdit {
  product_group: ProductGroup;
  pk_type: PkType;
  value: string;
  default_value: number | null;
  saving: boolean;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function CustomerNormsConfigPage() {
  const { isMobile, vw } = useViewport();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { warehouses, currentWarehouse, setWarehouseId } = useScope();

  const norms = useCurrentConfig('UPH_NORM');
  const customers = useCustomers();

  const [whFilter, setWhFilter] = useState<string>(currentWarehouse?.id ?? '');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [custSearch, setCustSearch] = useState('');
  const [editing, setEditing] = useState<CellEdit | null>(null);

  // Import dialog state
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState<ParseRow<UphImportRow>[]>([]);
  const [importSaving, setImportSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && !editing.saving) {
      editInputRef.current?.focus();
      editInputRef.current?.select();
    }
  }, [editing?.product_group, editing?.pk_type]);

  const targetWh = warehouses.find((w) => w.id === (whFilter || currentWarehouse?.id)) ?? null;
  const activeCustomers = customers.filter((c) => c.is_active);

  // Coverage map for all customers in selected warehouse
  const coverageMap = useMemo(() => {
    if (!targetWh) return new Map<string, Coverage>();
    const m = new Map<string, Coverage>();
    for (const c of activeCustomers) {
      m.set(c.id, computeCoverage(norms, targetWh.id, c.id));
    }
    return m;
  }, [norms, targetWh, activeCustomers]);

  // Filtered + sorted customer list (configured first, then partial, then none)
  const filteredCustomers = useMemo(() => {
    const needle = custSearch.toLowerCase();
    const list = needle
      ? activeCustomers.filter(
          (c) =>
            c.code.toLowerCase().includes(needle) || c.name.toLowerCase().includes(needle),
        )
      : activeCustomers;
    const order: Record<CoverageStatus, number> = { full: 0, partial: 1, none: 2 };
    return list.slice().sort((a, b) => {
      const ca = coverageMap.get(a.id)?.status ?? 'none';
      const cb = coverageMap.get(b.id)?.status ?? 'none';
      return order[ca] - order[cb] || a.code.localeCompare(b.code);
    });
  }, [activeCustomers, coverageMap, custSearch]);

  const selectedCustomer: Customer | null =
    activeCustomers.find((c) => c.id === selectedCustomerId) ?? filteredCustomers[0] ?? null;

  // Grid for selected customer
  const grid = useMemo(() => {
    if (!targetWh || !selectedCustomer) return new Map<string, ConfigValue>();
    const m = new Map<string, ConfigValue>();
    for (const n of norms) {
      const parts = n.key.split(':');
      if (parts[1] !== targetWh.id || parts[2] !== selectedCustomer.id) continue;
      m.set(`${parts[3]}:${parts[4]}`, n);
    }
    return m;
  }, [norms, targetWh, selectedCustomer]);

  // Default grid for showing fallback values
  const defaultGrid = useMemo(() => {
    if (!targetWh) return new Map<string, ConfigValue>();
    const m = new Map<string, ConfigValue>();
    for (const n of norms) {
      const parts = n.key.split(':');
      if (parts[1] !== targetWh.id || parts[2] !== 'DEFAULT') continue;
      m.set(`${parts[3]}:${parts[4]}`, n);
    }
    return m;
  }, [norms, targetWh]);

  // Coverage summary for selected customer
  const selectedCoverage = selectedCustomer
    ? (coverageMap.get(selectedCustomer.id) ?? { count: 0, status: 'none' as CoverageStatus })
    : null;

  // Stats
  const coveredCount = [...coverageMap.values()].filter((c) => c.status !== 'none').length;
  const fullCount = [...coverageMap.values()].filter((c) => c.status === 'full').length;

  async function commitEdit() {
    if (!editing || !user || !targetWh || !selectedCustomer) {
      setEditing(null);
      return;
    }
    const num = Math.max(1, Math.floor(Number(editing.value) || 0));
    if (!Number.isFinite(num) || num < 1) {
      setEditing(null);
      return;
    }
    const existing = grid.get(`${editing.product_group}:${editing.pk_type}`);
    if (existing && existing.value === num) {
      setEditing(null);
      return;
    }
    setEditing({ ...editing, saving: true });
    await configService.setValue({
      key: {
        kind: 'UPH_NORM',
        warehouse_id: targetWh.id,
        customer_id: selectedCustomer.id,
        product_group: editing.product_group,
        pk_type: editing.pk_type,
      },
      value: num,
      note: '',
      actor: user.email,
    });
    await auditService.log({
      actor: user.email,
      action: 'CONFIG_CHANGE',
      entity_type: 'config_value',
      entity_id: `uph:${targetWh.id}:${selectedCustomer.id}:${editing.product_group}:${editing.pk_type}`,
      summary: `Cập nhật UPH ${targetWh.code} · ${selectedCustomer.code} · ${editing.product_group} · ${editing.pk_type} thành ${num}`,
      payload: {
        warehouse_id: targetWh.id,
        customer_id: selectedCustomer.id,
        product_group: editing.product_group,
        pk_type: editing.pk_type,
        value: num,
      },
    });
    setEditing(null);
  }

  // ── Import handlers ──────────────────────────────────────────────────────

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const buf = ev.target?.result as ArrayBuffer;
      const result = parseSpreadsheet(buf, UphImportRowSchema, {
        headerMap: IMPORT_HEADER_MAP,
      });
      setImportRows(result.rows);
    };
    reader.readAsArrayBuffer(file);
    // reset so same file can be re-selected
    e.target.value = '';
  }

  async function confirmImport() {
    if (!targetWh || !selectedCustomer || !user) return;
    const valid = importRows.filter((r) => r.ok && r.data);
    setImportSaving(true);
    try {
      for (const r of valid) {
        const d = r.data!;
        await configService.setValue({
          key: {
            kind: 'UPH_NORM',
            warehouse_id: targetWh.id,
            customer_id: selectedCustomer.id,
            product_group: d.product_group,
            pk_type: d.pk_type,
          },
          value: d.uph,
          note: 'bulk-import',
          actor: user.email,
        });
      }
      await auditService.log({
        actor: user.email,
        action: 'IMPORT',
        entity_type: 'config_value',
        entity_id: `uph:${targetWh.id}:${selectedCustomer.id}`,
        summary: `Import ${valid.length} UPH norms cho ${selectedCustomer.code} · ${targetWh.code}`,
        payload: { rows: valid.length },
      });
      setImportRows([]);
      setImportOpen(false);
    } finally {
      setImportSaving(false);
    }
  }

  function downloadTemplate() {
    const templateRows = PRODUCT_GROUPS.flatMap((pg) =>
      PK_TYPES.map((pk) => ({ 'Nhóm hàng': pg, 'Loại pack': pk, UPH: 60 })),
    );
    const wb = buildSheet(templateRows, ['Nhóm hàng', 'Loại pack', 'UPH'], 'UPH_Template');
    downloadWorkbook(wb, 'uph-template.xlsx');
  }

  // ── Matrix table ─────────────────────────────────────────────────────────

  type Row = { product_group: ProductGroup } & Record<PkType, ConfigValue | undefined>;

  const matrixRows: Row[] = useMemo(
    () =>
      PRODUCT_GROUPS.map((pg) => {
        const r = { product_group: pg } as Row;
        for (const pk of PK_TYPES) r[pk] = grid.get(`${pg}:${pk}`);
        return r;
      }),
    [grid],
  );

  const matrixCols: Column<Row>[] = [
    {
      header: 'Nhóm hàng',
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600, color: 'var(--bx-text-dark)' }}>
            {PG_LABEL[r.product_group]}
          </div>
          <div style={{ fontSize: 11, color: 'var(--bx-text-muted)', fontFamily: 'var(--bx-font-mono)' }}>
            {r.product_group}
          </div>
        </div>
      ),
    },
    ...PK_TYPES.map(
      (pk): Column<Row> => ({
        header: (
          <div>
            <div>{PK_LABEL[pk]}</div>
            <div style={{ fontSize: 9.5, color: 'var(--bx-text-muted)', marginTop: 2 }}>{pk}</div>
          </div>
        ),
        align: 'right',
        render: (r) => {
          const cell = r[pk];
          const defCell = defaultGrid.get(`${r.product_group}:${pk}`);
          if (!targetWh || !selectedCustomer) return <span style={{ color: 'var(--bx-text-muted)' }}>—</span>;
          const displayVal = cell?.value ?? null;
          const defVal = defCell?.value ?? null;
          const isEditing = editing?.product_group === r.product_group && editing?.pk_type === pk;
          if (isEditing && editing) {
            return (
              <input
                ref={editInputRef}
                type="number"
                min={1}
                value={editing.value}
                disabled={editing.saving}
                onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                onBlur={commitEdit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    (e.target as HTMLInputElement).blur();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    setEditing(null);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  borderRadius: 6,
                  border: '1px solid var(--bx-primary)',
                  outline: 'none',
                  boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.15)',
                  fontFamily: 'var(--bx-font-mono)',
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: 13,
                  fontWeight: 600,
                  textAlign: 'right',
                  background: 'var(--bx-surface-1)',
                  color: 'var(--bx-text-dark)',
                }}
              />
            );
          }
          return (
            <button
              type="button"
              onClick={() =>
                setEditing({
                  product_group: r.product_group,
                  pk_type: pk,
                  value: String(displayVal ?? defVal ?? 60),
                  default_value: defVal,
                  saving: false,
                })
              }
              title={
                displayVal != null
                  ? `Đang dùng: ${displayVal} (riêng KH) — click để sửa`
                  : defVal != null
                    ? `Chưa cấu hình — sẽ dùng mặc định: ${defVal}. Click để ghi đè.`
                    : 'Chưa cấu hình — click để thêm'
              }
              style={{
                background: 'transparent',
                border: '1px solid transparent',
                cursor: 'pointer',
                fontFamily: 'var(--bx-font-mono)',
                fontVariantNumeric: 'tabular-nums',
                fontSize: 13,
                fontWeight: displayVal != null ? 600 : 400,
                color: displayVal != null ? 'var(--bx-text-dark)' : 'var(--bx-text-muted)',
                padding: '6px 8px',
                borderRadius: 6,
                width: '100%',
                textAlign: 'right',
                transition: 'background 120ms, border-color 120ms',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--bx-surface-2)';
                e.currentTarget.style.borderColor = 'var(--bx-border-muted)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = 'transparent';
              }}
            >
              {displayVal != null ? (
                displayVal
              ) : defVal != null ? (
                <span style={{ fontSize: 11 }}>({defVal})</span>
              ) : (
                '—'
              )}
            </button>
          );
        },
      }),
    ),
  ];

  // ── Import preview columns ───────────────────────────────────────────────

  type ImportDisplayRow = { row: number; ok: boolean; product_group: string; pk_type: string; uph: string; errors: string };
  const importDisplayRows: ImportDisplayRow[] = importRows.map((r) => ({
    row: r.row,
    ok: r.ok,
    product_group: String(r.raw['product_group'] ?? r.data?.product_group ?? ''),
    pk_type: String(r.raw['pk_type'] ?? r.data?.pk_type ?? ''),
    uph: String(r.data?.uph ?? r.raw['uph'] ?? ''),
    errors: r.errors?.join('; ') ?? '',
  }));

  const importCols: Column<ImportDisplayRow>[] = [
    {
      header: '#',
      width: 48,
      render: (r) => <span style={{ fontSize: 11, color: 'var(--bx-text-muted)' }}>R{r.row}</span>,
    },
    {
      header: '',
      width: 28,
      render: (r) =>
        r.ok ? (
          <Icon name="check" size={13} style={{ color: '#16a34a' }} />
        ) : (
          <Icon name="warning" size={13} style={{ color: '#dc2626' }} />
        ),
    },
    { header: 'Nhóm hàng', render: (r) => <span style={{ fontFamily: 'var(--bx-font-mono)', fontSize: 12 }}>{r.product_group}</span> },
    { header: 'Loại pack', render: (r) => <span style={{ fontFamily: 'var(--bx-font-mono)', fontSize: 12 }}>{r.pk_type}</span> },
    { header: 'UPH', align: 'right', mono: true, render: (r) => r.uph },
    {
      header: 'Lỗi',
      priority: 2,
      render: (r) =>
        r.errors ? (
          <span style={{ fontSize: 11, color: '#dc2626' }}>{r.errors}</span>
        ) : null,
    },
  ];

  // ── Render ───────────────────────────────────────────────────────────────

  const desktopLayout = vw >= 900;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader
        breadcrumb={['Cấu hình', 'Tham số vận hành', 'Định mức', 'Theo khách hàng']}
        title="Định mức UPH theo khách hàng"
        subtitle="Cấu hình riêng per khách hàng. Khách chưa có sẽ dùng định mức mặc định."
        actions={
          <>
            <Switcher
              icon="warehouse"
              label="Kho"
              value={targetWh?.id ?? ''}
              options={warehouses.map((w) => ({ value: w.id, label: w.code, sublabel: w.name }))}
              onChange={(v) => { setWhFilter(v); setWarehouseId(v); }}
              width={180}
            />
            <Button variant="outline" icon="grid" onClick={() => navigate('/admin/config/dinhmuc')}>
              Bảng tổng hợp
            </Button>
            <Button variant="outline" icon="layers" onClick={() => navigate('/admin/config/dinhmuc/mac-dinh')}>
              Định mức mặc định
            </Button>
          </>
        }
      />

      <div style={{ flex: 1, padding: isMobile ? 14 : 20 }}>
        {/* Summary metrics */}
        <div
          style={{
            display: 'grid',
            gap: 12,
            marginBottom: 16,
            gridTemplateColumns: vw < 720 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          }}
        >
          <Metric label="Khách hàng" value={activeCustomers.length} sublabel="đang hoạt động" />
          <Metric label="Đã cấu hình" value={coveredCount} sublabel="có ít nhất 1 cell" deltaTone="up" />
          <Metric label="Đầy đủ" value={fullCount} sublabel={`${TOTAL_CELLS} cells`} deltaTone="up" />
          <Metric
            label="Chưa cấu hình"
            value={activeCustomers.length - coveredCount}
            sublabel="dùng định mức mặc định"
          />
        </div>

        {/* Main layout: customer list + matrix */}
        <div
          style={{
            display: desktopLayout ? 'grid' : 'flex',
            gridTemplateColumns: desktopLayout ? '280px 1fr' : undefined,
            flexDirection: desktopLayout ? undefined : 'column',
            gap: 12,
          }}
        >
          {/* Customer list panel */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Card padding={0}>
              <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--bx-border-muted)' }}>
                <Input
                  icon="search"
                  placeholder="Tìm khách hàng…"
                  value={custSearch}
                  onChange={(e) => setCustSearch(e.target.value)}
                  size="sm"
                />
              </div>
              <div style={{ maxHeight: desktopLayout ? 520 : 240, overflowY: 'auto' }}>
                {filteredCustomers.length === 0 && (
                  <div style={{ padding: 20, textAlign: 'center', color: 'var(--bx-text-muted)', fontSize: 13 }}>
                    Không tìm thấy khách hàng
                  </div>
                )}
                {filteredCustomers.map((c) => {
                  const cov = coverageMap.get(c.id) ?? { count: 0, status: 'none' as CoverageStatus };
                  const isSelected = (selectedCustomerId || filteredCustomers[0]?.id) === c.id;
                  const badge = COVERAGE_BADGE[cov.status];
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelectedCustomerId(c.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        width: '100%',
                        padding: '9px 12px',
                        border: 'none',
                        borderBottom: '1px solid var(--bx-border-muted)',
                        background: isSelected ? 'var(--bx-surface-2)' : 'transparent',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--bx-text-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {c.code}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--bx-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {c.name}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
                        <Badge kind={badge.kind} size="sm">{badge.label}</Badge>
                        <span style={{ fontSize: 10, color: 'var(--bx-text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                          {cov.count}/{TOTAL_CELLS}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Card>
          </div>

          {/* Matrix panel */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {selectedCustomer ? (
              <>
                <Card
                  title={`${selectedCustomer.code} · ${targetWh?.code ?? '—'}`}
                  subtitle={
                    selectedCoverage
                      ? `${selectedCoverage.count}/${TOTAL_CELLS} cells đã cấu hình · Giá trị trong ngoặc = dùng mặc định`
                      : 'Chưa có cấu hình — click ô để thêm'
                  }
                  padding={0}
                  actions={
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button
                        variant="outline"
                        size="sm"
                        icon="download"
                        onClick={downloadTemplate}
                      >
                        Template
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        icon="upload"
                        onClick={() => setImportOpen(true)}
                      >
                        Import Excel
                      </Button>
                    </div>
                  }
                >
                  <Table
                    columns={matrixCols}
                    rows={matrixRows}
                    getRowKey={(r) => r.product_group}
                  />
                </Card>

                <Card style={{ padding: '10px 14px' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5, color: 'var(--bx-text-muted)' }}>
                    <Icon name="info" size={13} style={{ color: 'var(--bx-primary)', flexShrink: 0 }} />
                    <span>
                      Số thường = UPH riêng khách hàng này. Số trong ngoặc{' '}
                      <span style={{ fontFamily: 'var(--bx-font-mono)' }}>(60)</span> = chưa cấu hình, engine dùng định mức mặc định. Click ô để sửa inline · Enter để lưu · Esc để hủy.
                    </span>
                  </div>
                </Card>
              </>
            ) : (
              <Card>
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--bx-text-muted)' }}>
                  Chọn khách hàng bên trái để xem ma trận UPH
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Import dialog */}
      <Dialog
        open={importOpen}
        onClose={() => { setImportOpen(false); setImportRows([]); }}
        title={`Import UPH · ${selectedCustomer?.code ?? ''} · ${targetWh?.code ?? ''}`}
        subtitle="Tải file Excel/CSV với cột: product_group, pk_type, uph"
        width={620}
        footer={
          <>
            <Button variant="outline" onClick={() => { setImportOpen(false); setImportRows([]); }}>
              Hủy
            </Button>
            <Button
              variant="primary"
              icon="upload"
              onClick={confirmImport}
              disabled={importRows.filter((r) => r.ok).length === 0 || importSaving}
            >
              {importSaving
                ? 'Đang lưu…'
                : `Nhập ${importRows.filter((r) => r.ok).length} dòng hợp lệ`}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* File picker */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <Button
              variant="outline"
              icon="upload"
              onClick={() => fileRef.current?.click()}
            >
              Chọn file (.xlsx / .csv)
            </Button>
            <Button variant="ghost" size="sm" icon="download" onClick={downloadTemplate}>
              Tải template
            </Button>
          </div>

          {/* Column hint */}
          <div style={{ fontSize: 12, color: 'var(--bx-text-muted)', lineHeight: 1.6 }}>
            <div>Cột bắt buộc trong file:</div>
            <div style={{ fontFamily: 'var(--bx-font-mono)', fontSize: 11.5, marginTop: 4 }}>
              product_group &nbsp;|&nbsp; pk_type &nbsp;|&nbsp; uph
            </div>
            <div style={{ marginTop: 4 }}>
              Hoặc tiếng Việt: <span style={{ fontFamily: 'var(--bx-font-mono)', fontSize: 11.5 }}>Nhóm hàng | Loại pack | UPH</span>
            </div>
          </div>

          {/* Preview */}
          {importRows.length > 0 && (
            <div>
              <div style={{ display: 'flex', gap: 12, marginBottom: 8, fontSize: 12.5 }}>
                <span style={{ color: '#16a34a' }}>✓ {importRows.filter((r) => r.ok).length} hợp lệ</span>
                {importRows.filter((r) => !r.ok).length > 0 && (
                  <span style={{ color: '#dc2626' }}>
                    ✗ {importRows.filter((r) => !r.ok).length} lỗi (sẽ bỏ qua)
                  </span>
                )}
              </div>
              <div style={{ maxHeight: 260, overflowY: 'auto', borderRadius: 8, border: '1px solid var(--bx-border-muted)' }}>
                <Table
                  columns={importCols}
                  rows={importDisplayRows}
                  getRowKey={(r) => `${r.row}`}
                />
              </div>
            </div>
          )}
        </div>
      </Dialog>

      {/* Hidden file input handled via ref above */}
    </div>
  );
}
